import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFonts } from 'expo-font';
import { getEstampHistory } from '../services/estampsApi';
import AppHeader from '../components/AppHeader';

export default function HistoryScreen({ navigation }) {
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);


  const [fontsLoaded] = useFonts({
    'Poppins': require('../../assets/fonts/Poppins-Regular.ttf'),
    'Poppins-Medium': require('../../assets/fonts/Poppins-Medium.ttf'),
    'Poppins-SemiBold': require('../../assets/fonts/Poppins-SemiBold.ttf'),
    'Poppins-Bold': require('../../assets/fonts/Poppins-Bold.ttf'),
  });

  useEffect(() => {
    checkUserStatus();
  }, []);

  const checkUserStatus = async () => {
    try {
      const userToken = await AsyncStorage.getItem('userToken');
      const sessionId = await AsyncStorage.getItem('session_id');
      
      if (userToken && sessionId) {
        await loadTransactionHistory();
      } else {
        // If no authentication, redirect to login
        navigation.navigate('Login');
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      navigation.navigate('Login');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactionHistory = async () => {
    try {
      const result = await getEstampHistory();
      
      if (result.success) {
        setTransactionHistory(result.data || []);
      } else {
        console.error('Failed to load transaction history:', result.error);
        setTransactionHistory([]);
      }
    } catch (error) {
      console.error('Error loading transaction history:', error);
      setTransactionHistory([]);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadTransactionHistory();
    setRefreshing(false);
  };



  const renderHeader = () => (
    <AppHeader title="Riwayat Transaksi" onBack={() => navigation.goBack()} />
  );



  const renderHistoryContent = () => (
    <View style={styles.historyCard}>
      <Text style={styles.historyCardTitle}>Riwayat E-Stamp</Text>
      <View style={styles.historyContainer}>
        {transactionHistory.length > 0 ? (
          transactionHistory.map((transaction, index) => {
            // Handle date formatting - use created_date or fallback to timestamp
            let formattedDate = 'N/A';
            if (transaction.created_date) {
              formattedDate = new Date(transaction.created_date).toLocaleDateString('id-ID');
            } else if (transaction.timestamp) {
              formattedDate = new Date(transaction.timestamp * 1000).toLocaleDateString('id-ID');
            }
            
            // Determine transaction title
            const title = transaction.description || 
                         (transaction.brand_name ? `E-stamp collection for ${transaction.brand_name}` : 'E-Stamp Transaction');
            
            return (
              <View key={transaction.id || index} style={styles.historyItem}>
                <View style={styles.historyLeft}>
                  <Text style={styles.historyTitle}>{title}</Text>
                  <Text style={styles.historyDate}>{formattedDate}</Text>
                </View>
                <View style={styles.historyRight}>
                  <Text style={[styles.historyAmount, 
                    transaction.amount > 0 ? styles.historyAmountPositive : styles.historyAmountNegative
                  ]}>
                    {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                  </Text>
                  <Text style={styles.historySource}>{transaction.status || 'Unknown'}</Text>
                </View>
              </View>
            );
          })
        ) : (
          <Text style={styles.noHistoryText}>Belum ada riwayat transaksi</Text>
        )}
      </View>
    </View>
  );

  if (!fontsLoaded || loading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0F766E" />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {renderHeader()}
      
      <ScrollView 
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {renderHistoryContent()}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  content: {
    flex: 1,
    padding: 16,
  },
  historyCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  historyCardTitle: {
    fontSize: 18,
    fontFamily: 'Poppins-SemiBold',
    color: '#1E293B',
    marginBottom: 16,
  },
  historyContainer: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 12,
  },
  historyItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  historyLeft: {
    flex: 1,
  },
  historyTitle: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    color: '#1E293B',
    marginBottom: 2,
  },
  historyDate: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#64748B',
  },
  historyRight: {
    alignItems: 'flex-end',
  },
  historyAmount: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    marginBottom: 2,
  },
  historyAmountPositive: {
    color: '#10B981',
  },
  historyAmountNegative: {
    color: '#EF4444',
  },
  historySource: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#64748B',
  },
  noHistoryText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
    paddingVertical: 20,
  },

});